import React, { useState, useEffect, useCallback } from 'react';
import baseapi from '../../../services/baseapi';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Search, RefreshCw } from 'lucide-react';
import { ItemsPerPageDropdown } from '../../../components/ItemsPerPageDropdown ';

interface EventsTabProps {
  agentId: string;
  clientId: string;
}

const EventsTab: React.FC<EventsTabProps> = ({ agentId, clientId }) => {
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(30);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  // Reset page and search when cluster agent changes
  useEffect(() => {
    setCurrentPage(1);
    setSearch('');
    setDebouncedSearch('');
  }, [agentId, clientId]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const skip = (currentPage - 1) * itemsPerPage;
      const queryObj: any = {};

      if (debouncedSearch) {
        queryObj.$or = [
          { event_type: { "$ilike": `%${debouncedSearch}%` } },
          { type: { "$ilike": `%${debouncedSearch}%` } },
          { reason: { "$ilike": `%${debouncedSearch}%` } },
          { namespace: { "$ilike": `%${debouncedSearch}%` } },
          { involved_object: { "$ilike": `%${debouncedSearch}%` } },
          { object: { "$ilike": `%${debouncedSearch}%` } },
          { message: { "$ilike": `%${debouncedSearch}%` } }
        ];
      }

      const clusterName = new URLSearchParams(window.location.search).get('cluster_name');
      const params: any = {
        agent_id: agentId,
        client_id: clientId,
        skip,
        limit: itemsPerPage
      };

      if (clusterName) {
        params.cluster_name = clusterName;
      }

      if (Object.keys(queryObj).length > 0) {
        params.query = JSON.stringify(queryObj);
      }

      const res = await baseapi.get('/k8s_dashboard/events', { params });
      if (res.data?.status) {
        setEvents(res.data.data || []);
        setTotalItems(res.data.count || res.data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching events data:', error);
    } finally {
      setLoading(false);
    }
  }, [agentId, clientId, currentPage, itemsPerPage, debouncedSearch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    fetchData();
  };

  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + events.length;

  const handlePageChange = (page: number): void => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (value: number): void => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
        <div className="text-gray-500 font-mono text-[11px] bg-gray-900/50 px-3 py-1.5 rounded-lg border border-gray-800">
          {totalItems > 0 ? `${startIndex + 1} - ${endIndex} of ${totalItems}` : '0 events'}
        </div>
        <div className="flex gap-2 w-full sm:w-auto items-center">
          <button
            onClick={handleRefresh}
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-800/80 border border-gray-700 text-gray-400 hover:text-orange-400 hover:border-orange-500/50 hover:bg-orange-500/10 transition-all shadow-[0_2px_8px_rgba(0,0,0,0.2)]"
            title="Refresh events"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="search events..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-gray-900/50 border border-gray-700/50 text-gray-200 text-sm rounded-lg pl-9 pr-4 py-2 w-full sm:w-64 focus:outline-none focus:border-orange-500/50 transition-colors placeholder:text-gray-600 font-mono h-10"
            />
          </div>
        </div>
      </div>

      <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden flex flex-col">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-mono border-collapse">
            <thead>
              <tr className="border-b border-gray-700 bg-gray-800 sticky top-0">
                <th className="p-2 text-gray-100 uppercase tracking-wider font-normal">Type</th>
                <th className="p-2 text-gray-100 uppercase tracking-wider font-normal">Reason</th>
                <th className="p-2 text-gray-100 uppercase tracking-wider font-normal">Namespace</th>
                <th className="p-2 text-gray-100 uppercase tracking-wider font-normal">Object</th>
                <th className="p-2 text-gray-100 uppercase tracking-wider font-normal">Count</th>
                <th className="p-2 text-gray-100 uppercase tracking-wider font-normal">Time</th>
                <th className="p-2 text-gray-100 uppercase tracking-wider font-normal">Message</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <RefreshCw className="w-8 h-8 text-orange-500 animate-spin" />
                      <p className="text-gray-400 font-medium">Loading events...</p>
                    </div>
                  </td>
                </tr>
              ) : events.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-gray-500 italic">No events found</td>
                </tr>
              ) : (
                events.map((evt, idx) => {
                  const eventType = evt.event_type || evt.type;
                  const isWarning = eventType === 'Warning';
                  const isError = eventType?.toLowerCase().includes('fail') || eventType === 'Error';
                  
                  return (
                    <tr key={idx} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors">
                      <td className="p-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                          isWarning
                            ? 'bg-amber-500/10 border border-amber-500/30 text-amber-400'
                            : isError
                              ? 'bg-rose-500/10 border border-rose-500/30 text-rose-400'
                              : 'bg-gray-700 text-gray-300'
                        }`}>
                          {eventType}
                        </span>
                      </td>
                      <td className="p-2 text-gray-200">{evt.reason}</td>
                      <td className="p-2 text-gray-400">{evt.namespace}</td>
                      <td className="p-2 text-gray-300">{evt.involved_object || evt.object}</td>
                      <td className="p-2 text-gray-400">{evt.count || 1}</td>
                      <td className="p-2 text-gray-400">
                        {new Date(evt.event_time || evt.collected_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </td>
                      <td className="p-2 text-gray-400 max-w-md truncate" title={evt.message}>{evt.message}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Table Footer / Pagination */}
        <div className="bg-gray-800/80 border-t border-gray-700 px-3 py-2 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            <ItemsPerPageDropdown
              value={itemsPerPage}
              onChange={(val: any) => handleItemsPerPageChange(val)}
            />
            {totalItems > 0 ? (
              <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest font-mono">
                <span>{startIndex + 1}</span> - <span>{endIndex}</span> OF {totalItems}
              </span>
            ) : (
              events.length > 0 && (
                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest font-mono">
                  <span>{startIndex + 1}</span> - <span>{startIndex + events.length}</span>
                </span>
              )
            )}
          </div>
          <div className="flex items-center gap-1 font-mono">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="p-1 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
              title="First Page"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-1 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
              title="Previous Page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="px-2 py-0.5 bg-orange-600 rounded text-white text-[10px] font-bold mx-1 shadow-md shadow-orange-950/20">
              {currentPage}
            </div>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={events.length < itemsPerPage || (totalItems > 0 && currentPage === totalPages)}
              className="p-1 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
              title="Next Page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            {totalItems > 0 && (
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="p-1 text-gray-400 hover:text-white disabled:opacity-30 transition-colors"
                title="Last Page"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventsTab;
